import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './sic-nav.css.lit';
import { processMenuData } from './utility';
import { MenuNavItem } from './types';
import './sic-nav-link/sic-nav-link';
import './sic-nav-item/sic-nav-item';
import './sic-flyout/sic-flyout';

/**
 * The SicNav component
 * @element sic-main-nav
 */
@customElement('sic-nav')
export class SicNav extends OutlineElement {
  private _seed = Math.floor(Math.random() * 100000).toString();
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  static styles: CSSResultGroup = [componentStyles];
  screens = new ScreenSizeController(this);

  /**
   * Sets the aria label for the nav.
   */
  @property({ type: String, attribute: 'label' })
  label = 'main menu';

  /**
   * Generated array of MenuNavItem objects to build the menu.
   */
  @property({ type: Array, attribute: 'list-items' })
  listItems: Array<MenuNavItem>;

  /**
   * Unique Id for the nav menu.
   */
  @property({ type: String, reflect: true, attribute: 'id' })
  id = `menu-${this._seed}`;

  /**
   * URL used by setListItemData to fetch the JSON API menu item data.
   */
  @property({ type: String, attribute: 'data-url' })
  dataURL: string;

  firstUpdated() {
    this.setData();
  }

  render(): TemplateResult {
    const isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);

    return html`<div class="sic-nav">
      <nav aria-label="${this.label}" class="nav--main">
        <ul class="sic-nav nav--main-list ${isMobile ? 'mobile' : ''}">
          ${this.generateListItemsFromData(isMobile)}
        </ul>
      </nav>
    </div>`;
  }

  /**
   * Defaults to standard use case if no specific URL is passed.
   * @returns menu sub-components.
   */
  setData() {
    if (!this.dataURL) {
      this.dataURL = `${window.location.origin}/jsonapi/menu_items/main`;
    }
    return this.setListItemData(this.dataURL);
  }

  /**
   * Fetches data from url.
   * @param url
   * @returns json parsed response or error.
   */
  async fetchMenuData(url: string) {
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      throw new Error('no data');
    }
  }

  /**
   * Fetches JSON data from URL, and sets this.listItems with processed data.
   * Retries once after a short delay on failure so transient network errors
   * do not permanently leave the navigation empty.
   * @param url
   * @param isRetry - true when this is the automatic retry attempt
   */
  async setListItemData(url: string, isRetry = false) {
    try {
      const fetchResponse = await this.fetchMenuData(url);
      const data = processMenuData(fetchResponse);
      if (data?.length) {
        this.listItems = data;
      }
    } catch (error) {
      if (!isRetry) {
        console.warn('[sic-nav] Menu data fetch failed; retrying in 3 s.', error);
        this._retryTimer = setTimeout(() => {
          this._retryTimer = null;
          this.setListItemData(url, true);
        }, 3000);
      } else {
        console.error(
          '[sic-nav] Menu data fetch failed after retry. Navigation will remain empty.',
          error
        );
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._retryTimer !== null) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  /**
   *
   * @param isMobile
   * @returns <li> with appropriate menu sub-component.
   */
  generateListItemsFromData(isMobile: boolean) {
    return this.listItems?.map((listItem: MenuNavItem) => {
      const hasSubList = Object?.keys(listItem)?.includes('sub') ? true : false;
      if (hasSubList && !isMobile) {
        return html` <li>
          <sic-flyout
            toplevel
            .parentID=${this.id}
            .item=${listItem}
            id=${listItem.id}
          ></sic-flyout>
        </li>`;
      }
      if (hasSubList && isMobile) {
        return html` <li>
          <sic-nav-item
            toplevel
            .parentID=${this.id}
            .item=${listItem}
            id=${listItem.id}
          ></sic-nav-item>
        </li>`;
      } else {
        return html` <li>
          <sic-nav-link
            toplevel
            id=${listItem.id}
            .item=${listItem}
            slot="list"
          ></sic-nav-link>
        </li>`;
      }
    });
  }
}
